import React from "react";
import {
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronUpDownIcon,
} from "@heroicons/react/24/outline";
import { ChevronDown, ChevronUp } from "lucide-react";
import LoadingIndicator from "@/components/LoadingIndicator/LoadingIndicator";
import { MPDashboardZipCode } from "../../api/getMPDashboard/types";
import { QualificationStatus } from "../../api/exportMPDashboardCsv/types";
import { SortDirection, SortField } from "../../hooks/useMPDashboard";

interface MPDashboardTableProps {
  items: MPDashboardZipCode[];
  totalFilteredCount: number;
  loading: boolean;
  expanded: boolean;
  canExpand: boolean;
  sortField: SortField;
  sortDirection: SortDirection;
  onSort: (field: SortField) => void;
  onToggleExpand: (expanded: boolean) => void;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat("en-US").format(value);
}

function formatPercentage(value: number): string {
  return `${value.toFixed(2)}%`;
}

const STATUS_CONFIG: Record<
  QualificationStatus,
  { label: string; className: string }
> = {
  recommend: {
    label: "Recommend",
    className:
      "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  },
  review: {
    label: "Review",
    className:
      "bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  },
  low: {
    label: "Low",
    className: "bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-gray-400",
  },
};

interface SortableHeaderCellProps {
  label: string;
  field: SortField;
  activeField: SortField;
  direction: SortDirection;
  onSort: (field: SortField) => void;
}

const SortableHeaderCell: React.FC<SortableHeaderCellProps> = ({
  label,
  field,
  activeField,
  direction,
  onSort,
}) => {
  const isActive = field === activeField;

  let sortIcon: React.ReactNode;
  if (!isActive) {
    sortIcon = (
      <ChevronUpDownIcon className="h-4 w-4 text-gray-400 dark:text-slate-500" />
    );
  } else if (direction === "desc") {
    sortIcon = (
      <ChevronDownIcon className="h-4 w-4 text-gray-500 dark:text-slate-400" />
    );
  } else {
    sortIcon = (
      <ChevronUpIcon className="h-4 w-4 text-gray-500 dark:text-slate-400" />
    );
  }

  return (
    <th
      className="h-11 px-4 align-middle font-semibold text-gray-600 dark:text-slate-300 cursor-pointer select-none"
      onClick={() => onSort(field)}
    >
      <div className="flex items-center gap-1">
        {label}
        {sortIcon}
      </div>
    </th>
  );
};

const MarketPresenceBar: React.FC<{ value: number }> = ({ value }) => {
  const cappedWidth = Math.min(value * 20, 100);
  const isAboveThreshold = value >= 1.0;

  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-16 overflow-hidden rounded-full bg-gray-200 dark:bg-slate-700">
        <div
          className={`h-full rounded-full transition-all ${
            isAboveThreshold
              ? "bg-emerald-500 dark:bg-emerald-400"
              : "bg-amber-400 dark:bg-amber-500"
          }`}
          style={{ width: `${cappedWidth}%` }}
        />
      </div>
      <span
        className={`text-sm tabular-nums ${
          isAboveThreshold
            ? "text-emerald-700 dark:text-emerald-400"
            : "text-gray-900 dark:text-white"
        }`}
      >
        {formatPercentage(value)}
      </span>
    </div>
  );
};

const MPDashboardTable: React.FC<MPDashboardTableProps> = ({
  items,
  totalFilteredCount,
  loading,
  expanded,
  canExpand,
  sortField,
  sortDirection,
  onSort,
  onToggleExpand,
}) => {
  return (
    <div className="relative space-y-4">
      {loading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-white bg-opacity-75 dark:bg-slate-800 dark:bg-opacity-75">
          <LoadingIndicator isFullScreen={false} />
        </div>
      )}

      <div className="overflow-x-auto rounded-md border border-gray-200 bg-white dark:bg-gray-800 dark:border-white/20">
        <div className="relative w-full">
          <table className="w-full caption-bottom text-sm text-gray-900 dark:text-slate-100">
            <thead className="[&_tr]:border-b [&_tr]:border-gray-200 dark:[&_tr]:border-slate-700">
              <tr className="bg-gray-50 dark:bg-slate-700/50">
                <SortableHeaderCell
                  activeField={sortField}
                  direction={sortDirection}
                  field="zipcode"
                  label="Zip Code"
                  onSort={onSort}
                />
                <SortableHeaderCell
                  activeField={sortField}
                  direction={sortDirection}
                  field="totalSales"
                  label="Total Sales"
                  onSort={onSort}
                />
                <SortableHeaderCell
                  activeField={sortField}
                  direction={sortDirection}
                  field="totalCustomers"
                  label="Customers"
                  onSort={onSort}
                />
                <SortableHeaderCell
                  activeField={sortField}
                  direction={sortDirection}
                  field="totalHouseholds"
                  label="Households"
                  onSort={onSort}
                />
                <SortableHeaderCell
                  activeField={sortField}
                  direction={sortDirection}
                  field="marketPresence"
                  label="Market Presence"
                  onSort={onSort}
                />
                <th className="h-11 px-4 align-middle font-semibold text-gray-600 dark:text-slate-300">
                  <div className="flex items-center">Status</div>
                </th>
              </tr>
            </thead>

            <tbody className="[&_tr:last-child]:border-0">
              {items.length === 0 && !loading ? (
                <tr className="border-b border-gray-200 dark:border-slate-700">
                  <td
                    className="p-10 text-center text-gray-500 dark:text-slate-400"
                    colSpan={6}
                  >
                    No zip code data available
                  </td>
                </tr>
              ) : (
                items.map((item) => {
                  const statusConfig =
                    STATUS_CONFIG[
                      item.qualificationStatus as QualificationStatus
                    ];
                  return (
                    <tr
                      className="border-b border-gray-200 transition-colors hover:bg-gray-50 dark:border-slate-700 dark:hover:bg-slate-700"
                      key={item.zipcode}
                    >
                      <td className="p-4 align-middle">{item.zipcode}</td>
                      <td className="p-4 align-middle tabular-nums">
                        {formatCurrency(item.totalSales)}
                      </td>
                      <td className="p-4 align-middle tabular-nums">
                        {formatNumber(item.totalCustomers)}
                      </td>
                      <td className="p-4 align-middle tabular-nums">
                        {formatNumber(item.totalHouseholds)}
                      </td>
                      <td className="p-4 align-middle">
                        <MarketPresenceBar value={item.marketPresence} />
                      </td>
                      <td className="p-4 align-middle">
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-xs ${statusConfig.className}`}
                        >
                          {statusConfig.label}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {!loading && canExpand && (
          <div className="relative">
            {!expanded && (
              <div className="pointer-events-none absolute -top-12 left-0 right-0 h-12 bg-gradient-to-t from-white dark:from-slate-800" />
            )}
            <div className="border-t border-gray-200 bg-gray-50 dark:border-slate-700 dark:bg-slate-700/50">
              <div className="flex justify-center py-3">
                <button
                  className="inline-flex items-center gap-2 h-9 px-3 py-1 rounded-md border shadow-sm text-sm font-medium transition-colors bg-white border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-white/20 dark:text-white dark:hover:border-white/30 dark:hover:bg-gray-700"
                  onClick={() => onToggleExpand(!expanded)}
                >
                  {expanded ? (
                    <>
                      <ChevronUp className="h-4 w-4" />
                      Show Less
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-4 w-4" />
                      Show All {totalFilteredCount} Zip Codes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 px-1 pt-3 text-sm text-gray-500 dark:text-gray-400">
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2 w-2 rounded-full bg-emerald-500 dark:bg-emerald-400" />
          Recommend = &ge;1% presence + &ge;$25k sales
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2 w-2 rounded-full bg-amber-400 dark:bg-amber-500" />
          Review = partial criteria met
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2 w-2 rounded-full bg-gray-400 dark:bg-gray-500" />
          Low = below thresholds
        </span>
      </div>
    </div>
  );
};

export default MPDashboardTable;
